/* ScratchCard — Canvas 2D foil reveal with pointer events.
   props:
     foilColor (string), prizeMessage (string), isWin (bool), onReveal (fn)
   - retour haptique continu pendant le grattage, note franche à la révélation
   - auto-reveal at 60% scratched
   - confettis + feu d'artifice on isWin reveal (voir lib/winFx)
   - respects prefers-reduced-motion
*/
import { useEffect, useRef, useState, useCallback } from "react";
import { toPng } from "html-to-image";
import { runWinFx } from "@/lib/winFx";
import { Download, Loader2, X } from "lucide-react";

// Part de surface à gratter avant l'auto-révélation. Plus la valeur est haute,
// plus le grattage est « lent » (l'utilisateur doit gratter davantage).
const REVEAL_AT = 0.5;
// Rayon minimal du pinceau de grattage (px). Un pinceau plus petit découvre
// moins de surface par passage → grattage plus progressif.
//
// Il ne peut PAS rester fixe depuis que le ticket occupe tout l'écran : sur une
// surface trois fois plus grande, atteindre REVEAL_AT au pinceau de 24 px
// demandait une dizaine de balayages, et le jeu tournait à la corvée. Le rayon
// suit donc la largeur du ticket, ce plancher ne servant qu'aux écrans étroits.
const BRUSH_RADIUS = 24;
// Largeur du ticket rapportée au rayon du pinceau : ~12 traits pour couvrir
// l'écran de gauche à droite, quelle que soit la taille du téléphone.
const BRUSH_WIDTH_RATIO = 24;
// Retour haptique : impulsions très courtes répétées tant que le doigt bouge,
// pour imiter le grain d'un vrai ticket. Chaque appel à navigator.vibrate()
// remplace la vibration en cours — enchaînées, elles donnent un grésillement
// continu plutôt qu'une suite de coups.
const HAPTIC_PULSE_MS = 12;    // durée d'une impulsion
const HAPTIC_INTERVAL_MS = 55; // cadence maximale (au-delà, le moteur sature)
// Cadence de mesure de la surface grattée. `getImageData` lit tout le canvas :
// en plein écran sur un mobile haute densité, cela fait plusieurs millions de
// pixels par appel. Une chance sur sept à chaque déplacement du doigt — l'ancien
// réglage — revenait à le faire dix fois par seconde, et le grattage saccadait
// sur un téléphone d'entrée de gamme. Le plafond de temps borne le coût quelle
// que soit la taille de la surface ou la cadence du tactile.
const PROGRESS_SAMPLE_MS = 250;
// Couleurs du ticket (format maquette) : message principal en bleu vif,
// encart des modalités de récupération en lavande.
const MESSAGE_BLUE = "#2438E8";
const TERMS_BG = "#E9E4F9";

export default function ScratchCard({
  foilColor = "#FFC247",
  prizeMessage = "",
  isWin = false,
  sponsor = null,
  sponsorLabel = null,
  prizeImageUrl = null,
  collectionTerms = null,
  onReveal,
  onClose,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const ticketRef = useRef(null);
  const saveBtnRef = useRef(null);
  const closeBtnRef = useRef(null);
  const stateRef = useRef({ drawing: false, revealed: false });
  const brushRef = useRef(BRUSH_RADIUS);
  const lastProgressRef = useRef(0);
  const [progress, setProgress] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [saving, setSaving] = useState(false);

  /* Le bloc visuel du haut porte, dans cet ordre : le logo du sponsor, sinon le
     visuel du lot gagné, sinon un emoji. On ne le resserre après grattage que
     dans les deux derniers cas... sauf s'il s'agit justement du lot : c'est ce
     que le gagnant vient regarder, le réduire serait contre-productif. */
  const showsPrizeImage = !sponsor?.logoUrl && isWin && !!prizeImageUrl;
  const heroCompact = revealed && !showsPrizeImage;

  // Numéro de ticket figé au montage (sinon il changerait à chaque re-render)
  const ticketNo = useRef(1000 + Math.floor(Math.random() * 9000));
  const prefersReduced = useRef(
    typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  /* Vibration. Absente sur iOS (Safari n'implémente pas l'API) : l'appel est
     alors sans effet, jamais une erreur. */
  const lastHapticRef = useRef(0);
  const vibrate = useCallback((pattern) => {
    try {
      navigator.vibrate && navigator.vibrate(pattern);
    } catch (_) {
      /* no-op */
    }
  }, []);

  // Grésillement pendant le grattage, limité en cadence : sans throttle on
  // saturerait le moteur haptique et la sensation deviendrait un buzz continu.
  const scratchHaptic = useCallback(() => {
    // Garde sur la ref, pas sur l'état React : celui-ci est mis à jour de façon
    // asynchrone et une impulsion de grattage écraserait la note de révélation.
    if (stateRef.current.revealed) return;
    const now = Date.now();
    if (now - lastHapticRef.current < HAPTIC_INTERVAL_MS) return;
    lastHapticRef.current = now;
    vibrate(HAPTIC_PULSE_MS);
  }, [vibrate]);

  // Rien ne doit continuer à vibrer si le ticket se ferme en plein grattage.
  useEffect(() => () => vibrate(0), [vibrate]);

  /* Confettis puis feu d'artifice. La poignée d'arrêt est gardée en ref : le
     spectacle doit pouvoir être coupé avant une capture et au démontage — un
     ticket fermé en plein tir laisserait sinon des particules sur la page. */
  const fxStopRef = useRef(null);
  const stopWinFx = useCallback(() => {
    fxStopRef.current && fxStopRef.current();
    fxStopRef.current = null;
  }, []);
  const fireWinFx = useCallback(() => {
    if (prefersReduced.current) return;
    stopWinFx();
    fxStopRef.current = runWinFx({ colors: [foilColor] });
  }, [foilColor, stopWinFx]);

  useEffect(() => stopWinFx, [stopWinFx]);

  const doReveal = useCallback(() => {
    if (stateRef.current.revealed) return;
    stateRef.current.revealed = true;
    const cv = canvasRef.current;
    if (cv) {
      cv.style.transition = "opacity 280ms ease";
      cv.style.opacity = "0";
    }
    setRevealed(true);
    // Le grésillement s'arrête, remplacé par une note franche : rythmée si
    // c'est gagné, une seule impulsion sinon.
    vibrate(isWin ? [0, 45, 60, 45, 60, 110] : 30);
    if (isWin) fireWinFx();
    onReveal && onReveal();
  }, [isWin, fireWinFx, onReveal, vibrate]);

  // Initial paint of foil
  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    cv.width = rect.width * window.devicePixelRatio;
    cv.height = rect.height * window.devicePixelRatio;
    const ctx = cv.getContext("2d");
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    brushRef.current = Math.max(
      BRUSH_RADIUS,
      Math.round(rect.width / BRUSH_WIDTH_RATIO)
    );

    // Paint solid foil + subtle diagonal sheen
    ctx.fillStyle = foilColor;
    ctx.fillRect(0, 0, rect.width, rect.height);
    const grad = ctx.createLinearGradient(0, 0, rect.width, rect.height);
    grad.addColorStop(0, "rgba(255,255,255,0.18)");
    grad.addColorStop(0.5, "rgba(255,255,255,0.0)");
    grad.addColorStop(1, "rgba(0,0,0,0.18)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, rect.width, rect.height);

    // "Grattez ici" label
    ctx.fillStyle = "rgba(22,24,29,0.55)";
    // Le libellé suit lui aussi la surface : 14 px se perdaient au milieu d'un
    // vernis plein écran.
    ctx.font = `600 ${Math.round(
      Math.min(22, Math.max(14, rect.width / 24))
    )}px 'Sora', sans-serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("✦ Grattez ici ✦", rect.width / 2, rect.height / 2);

    // If reduced motion: auto reveal
    if (prefersReduced.current) {
      setTimeout(doReveal, 200);
    }
  }, [foilColor, doReveal]);

  const computeProgress = useCallback(() => {
    const cv = canvasRef.current;
    if (!cv) return 0;
    const ctx = cv.getContext("2d");
    const w = cv.width;
    const h = cv.height;
    // Sample 60x60 grid for performance
    const step = Math.max(1, Math.floor(w / 60));
    const data = ctx.getImageData(0, 0, w, h).data;
    let transparent = 0;
    let total = 0;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        const i = (y * w + x) * 4 + 3; // alpha
        total++;
        if (data[i] < 128) transparent++;
      }
    }
    return total ? transparent / total : 0;
  }, []);

  const scratch = useCallback(
    (x, y) => {
      const cv = canvasRef.current;
      if (!cv) return;
      const ctx = cv.getContext("2d");
      ctx.globalCompositeOperation = "destination-out";
      ctx.beginPath();
      ctx.arc(x, y, brushRef.current, 0, Math.PI * 2);
      ctx.fill();
    },
    []
  );

  const handlePointerDown = (e) => {
    if (revealed) return;
    stateRef.current.drawing = true;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    scratch(x, y);
    scratchHaptic();
  };
  const handlePointerMove = (e) => {
    if (!stateRef.current.drawing || revealed) return;
    const rect = canvasRef.current.getBoundingClientRect();
    scratch(e.clientX - rect.left, e.clientY - rect.top);
    scratchHaptic(); // le grain se sent tant que le doigt bouge
    const now = Date.now();
    if (now - lastProgressRef.current >= PROGRESS_SAMPLE_MS) {
      lastProgressRef.current = now;
      const p = computeProgress();
      setProgress(p);
      if (p >= REVEAL_AT) doReveal();
    }
  };
  const handlePointerUp = () => {
    if (!stateRef.current.drawing) return;
    stateRef.current.drawing = false;
    vibrate(0); // le doigt se lève : plus rien ne doit vibrer
    const p = computeProgress();
    setProgress(p);
    if (p >= REVEAL_AT) doReveal();
  };

  /* Le ticket révélé est plus haut que l'écran d'un petit téléphone : même une
     fois l'overlay rendu défilant, le bouton de sauvegarde reste sous la ligne
     de flottaison. On l'amène donc dans le champ de vision — `block: "nearest"`
     ne bouge rien s'il est déjà visible, et le délai laisse la révélation se
     poser avant de déplacer la vue. */
  useEffect(() => {
    if (!revealed || !isWin) return;
    const t = setTimeout(() => {
      saveBtnRef.current?.scrollIntoView({
        behavior: prefersReduced.current ? "auto" : "smooth",
        block: "nearest",
      });
    }, 400);
    return () => clearTimeout(t);
  }, [revealed, isWin]);

  /* Capture du ticket gagnant en PNG : partage natif sur mobile
     (permet « Enregistrer l'image »), sinon téléchargement direct. */
  const saveTicket = useCallback(async () => {
    const node = ticketRef.current;
    if (!node || saving) return;
    setSaving(true);
    try {
      /* Le spectacle s'éteint AVANT la capture, et on laisse le navigateur
         repeindre. Le PNG, lui, n'a jamais rien à craindre : son canvas vit
         dans <body>, hors du sous-arbre cloné. C'est l'écran qu'on nettoie —
         le passager déclenche souvent la capture système du téléphone dans la
         foulée, et celle-là photographie tout ce qui est encore affiché. */
      stopWinFx();
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#ffffff",
        /* La croix de fermeture et le film à gratter sont posés DANS le ticket
           — c'est ce qui leur permet d'être positionnés dessus — mais ils
           appartiennent à l'interface, pas au souvenir que le passager garde.
           `filter` écarte un nœud et tout son sous-arbre du clone capturé.

           Le canvas est déjà en opacity 0 après révélation, donc invisible ;
           l'exclure évite surtout de sérialiser une surface plein format pour
           rien. */
        filter: (n) => n !== closeBtnRef.current && n !== canvasRef.current,
      });
      const fileName = `ticket-gagnant-gocab-${Date.now()}.png`;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: "Mon ticket gagnant GoCab" });
        } catch (_) {
          /* partage annulé par l'utilisateur — pas d'erreur à afficher */
        }
        return;
      }
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = fileName;
      a.click();
    } catch (err) {
      console.error("Capture du ticket impossible", err);
    } finally {
      setSaving(false);
    }
  }, [saving, stopWinFx]);

  return (
    <div
      ref={containerRef}
      className="relative flex w-full flex-1 flex-col select-none bg-white"
      data-testid="scratch-card"
    >
      {/* Ticket — plus une carte posée sur un fond, mais la page elle-même :
          bord à bord, sans coins arrondis ni ombre, puisqu'il n'y a plus rien
          derrière lui à faire deviner. */}
      <div
        ref={ticketRef}
        className="relative flex flex-1 flex-col overflow-hidden bg-white"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        {onClose && (
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            data-testid="scratch-dialog-close"
            aria-label="Fermer"
            className="absolute z-30 grid place-items-center rounded-full bg-[#16181D] text-white shadow-[0_4px_20px_rgba(0,0,0,0.35)] ring-[3px] ring-white transition-transform hover:scale-105 active:scale-95 touch-manipulation"
            style={{
              top: "max(clamp(0.65rem, 2.8vw, 1rem), env(safe-area-inset-top))",
              right: "max(clamp(0.65rem, 2.8vw, 1rem), env(safe-area-inset-right))",
              width: "clamp(2.75rem, 12vw, 3.25rem)",
              height: "clamp(2.75rem, 12vw, 3.25rem)",
            }}
          >
            <X
              className="shrink-0"
              style={{
                width: "clamp(1.25rem, 5.5vw, 1.5rem)",
                height: "clamp(1.25rem, 5.5vw, 1.5rem)",
              }}
              strokeWidth={3}
            />
          </button>
        )}
        {/* Bandeau sponsor — au-dessus du vernis et non dessous : il doit se
            lire dès l'ouverture, avant le premier coup d'ongle. */}
        {sponsorLabel && sponsor && (
          <div
            className="px-12 pt-3 text-center text-[10px] uppercase tracking-[0.22em] font-mono text-black/50"
            data-testid="scratch-sponsor-banner"
          >
            {sponsorLabel} <span className="font-semibold">{sponsor.name}</span>
          </div>
        )}

        {/* Partie haute (zone grattable) — généreuse AVANT le grattage : c'est la
            surface que le passager gratte réellement, elle porte toute la
            sensation du jeu, et sa taille doit être la même que le ticket soit
            gagnant ou perdant, sinon la hauteur du modal trahit l'issue.

            Une fois révélé, ces deux contraintes tombent : le résultat est connu
            et il n'y a plus rien à gratter. On resserre donc, ce qui rapproche
            surtout le bouton de sauvegarde du haut de l'écran. */}
        <div
          className={`relative flex flex-1 flex-col justify-center px-6 transition-[padding] duration-300 motion-reduce:transition-none ${
            revealed ? "pt-6 pb-5" : "pt-9 pb-8"
          }`}
          style={{
            paddingTop: onClose && !revealed ? "clamp(3.25rem, 14vw, 3.75rem)" : undefined,
          }}
        >
          <div
            className="relative z-0 text-center"
            data-testid="win-result"
            aria-live="polite"
          >
            {/* Numéro de ticket */}
            <div className="text-xs font-medium uppercase tracking-[0.35em] text-black">
              Ticket {ticketNo.current}
            </div>

            {/* Bloc visuel — hauteur réservée tant que le ticket n'est pas
                gratté, pour que gagnant et perdant occupent la même place.

                Il ne se resserre PAS quand il porte le visuel du lot : c'est
                précisément ce que le gagnant vient regarder. Un logo sponsor,
                lui, est déjà repris en pied de ticket sous « Offert par ». */}
            <div
              className={`flex items-center justify-center transition-all duration-300 motion-reduce:transition-none ${
                heroCompact ? "mt-3 h-14" : "mt-5 h-24"
              }`}
            >
              {sponsor?.logoUrl ? (
                <img
                  src={sponsor.logoUrl}
                  alt={sponsor.name}
                  data-testid="ticket-sponsor-logo"
                  className={`max-w-[210px] object-contain transition-all duration-300 motion-reduce:transition-none ${
                    heroCompact ? "h-12" : "h-20"
                  }`}
                />
              ) : isWin && prizeImageUrl ? (
                <img
                  src={prizeImageUrl}
                  alt="Lot"
                  data-testid="prize-image"
                  className="h-20 max-w-[200px] object-contain"
                />
              ) : (
                <div className={heroCompact ? "text-4xl" : "text-5xl"}>
                  {isWin ? "🎉" : "🎟️"}
                </div>
              )}
            </div>

            {/* Message principal en bleu vif */}
            <div
              className={`flex items-center justify-center font-heading text-[26px] font-bold leading-tight px-1 transition-all duration-300 motion-reduce:transition-none ${
                revealed ? "mt-4 min-h-0" : "mt-6 min-h-[4.5rem]"
              }`}
              style={{ color: MESSAGE_BLUE }}
            >
              {prizeMessage}
            </div>

            {/* Sous-texte — même réserve de hauteur, libérée après révélation. */}
            <div
              className={`text-[15px] text-black transition-all duration-300 motion-reduce:transition-none ${
                revealed ? "mt-3 min-h-0" : "mt-4 min-h-[2rem]"
              }`}
            >
              {isWin
                ? "Conservez cet écran. Vous serez recontacté"
                : "Tentez encore en scannant un autre véhicule GoCab."}
            </div>
          </div>

          {/* Couche supérieure : le film à gratter.

              Une fois révélé, il n'est que transparent — il reste posé sur
              toute la zone du ticket. Sans le neutraliser, son `touch-none`
              continuait d'absorber le tactile : le passager ne pouvait plus
              faire défiler en glissant sur le ticket, et n'atteignait donc pas
              le bouton de sauvegarde sur un petit écran. */}
          <canvas
            ref={canvasRef}
            data-testid="scratch-canvas"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={handlePointerUp}
            aria-hidden={revealed}
            className={`absolute inset-0 w-full h-full ${
              revealed
                ? "pointer-events-none"
                : "touch-none cursor-grab active:cursor-grabbing"
            }`}
            style={{ borderRadius: "inherit" }}
          />
        </div>

        {/* Partie basse : rendue UNIQUEMENT après grattage pour ne pas trahir
            l'issue (les modalités n'existent que sur un ticket gagnant). */}
        {revealed && (
          <div className="pb-5">
            {/* Ligne de perforation à gros points noirs, encoches sur les bords */}
            <div className="relative flex items-center justify-between px-7 py-3">
              <span className="absolute -left-3.5 h-7 w-7 rounded-full bg-black" />
              {Array.from({ length: 8 }).map((_, i) => (
                <span key={i} className="h-3 w-3.5 rounded-full bg-black" />
              ))}
              <span className="absolute -right-3.5 h-7 w-7 rounded-full bg-black" />
            </div>

            {/* Encart lavande : modalités de récupération du lot */}
            {isWin && collectionTerms && (
              <div className="px-4 mt-1">
                <div
                  className="rounded-2xl px-4 py-3 text-center text-sm leading-snug text-black"
                  style={{ background: TERMS_BG }}
                  data-testid="collection-terms"
                >
                  {collectionTerms}
                </div>
              </div>
            )}

            {/* Mention sponsor */}
            {sponsor && (
              <div
                className="mt-5 flex items-center justify-center gap-3"
                data-testid="ticket-sponsor"
              >
                <span className="text-xs font-semibold uppercase tracking-[0.3em] text-black">
                  Offert par
                </span>
                {sponsor.logoUrl ? (
                  <img
                    src={sponsor.logoUrl}
                    alt={sponsor.name}
                    className="h-6 max-w-[100px] object-contain"
                  />
                ) : (
                  <span className="text-xs font-semibold text-black">{sponsor.name}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pied de page — hors du ticket capturé en image. Il repose maintenant
          sur le blanc du ticket et non plus sur le voile sombre : les teintes
          claires d'avant y étaient devenues invisibles. */}
      <div
        className="px-4 pt-4"
        style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex-1 h-1.5 rounded-full bg-black/10 overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${Math.min(100, Math.round(progress * 100))}%`,
                background: "var(--color-brand)",
              }}
            />
          </div>
          {!revealed && (
            <button
              type="button"
              onClick={doReveal}
              data-testid="reveal-direct-btn"
              className="text-xs underline underline-offset-4 text-black/50 hover:text-black"
            >
              Révéler directement
            </button>
          )}
        </div>

        {/* Sauvegarde du ticket gagnant en image.

            Aplat plein et non plus contour teinté : c'est l'action la plus
            importante de tout le parcours, elle doit se voir immédiatement.

            Les couleurs viennent de la charte : `--primary` porte la couleur
            configurée (ou celle du sponsor en cas de takeover) et
            `--primary-foreground` la couleur de texte que `applyBrand` a calculée
            pour elle — le libellé reste donc lisible quelle que soit la teinte
            saisie par l'annonceur. */}
        {revealed && isWin && (
          <button
            ref={saveBtnRef}
            type="button"
            onClick={saveTicket}
            disabled={saving}
            data-testid="save-ticket-btn"
            className="mt-3 w-full h-12 rounded-full font-heading font-semibold text-sm inline-flex items-center justify-center gap-2 shadow-lg transition-all active:scale-[0.98] disabled:opacity-60"
            style={{
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
            }}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Capture en cours…
              </>
            ) : (
              <>
                <Download className="h-4 w-4" /> Sauvegarder mon ticket
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}
